import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, 
  Table2, 
  List, 
  Download, 
  Search,
  Edit2,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Eye,
  FolderPlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExcelTable from "@/components/ExcelTable";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [creatingProject, setCreatingProject] = useState(false);
  
  const [extraction, setExtraction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [isEditingFields, setIsEditingFields] = useState(false);
  const [editedFields, setEditedFields] = useState([]);

  const fetchExtraction = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/extraction/${id}`);
      setExtraction(response.data);
      setEditedFields(response.data.fields || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.response?.data?.detail || "Failed to load extraction results");
      toast.error("Failed to load results");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchExtraction();
  }, [fetchExtraction]);

  // Search functionality
  const filteredPages = useMemo(() => {
    if (!extraction?.pages || !searchQuery.trim()) {
      return extraction?.pages || [];
    }
    
    const query = searchQuery.toLowerCase();
    return extraction.pages.filter(page => 
      page.text.toLowerCase().includes(query)
    );
  }, [extraction?.pages, searchQuery]);

  // Highlight search matches
  const highlightText = (text, query) => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} className="search-highlight">{part}</mark>
        : part
    );
  };

  // Edit fields
  const handleFieldChange = (index, field, value) => {
    setEditedFields(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const saveFields = async () => {
    try {
      await axios.put(`${API}/extraction/${id}/fields`, editedFields);
      setExtraction(prev => ({ ...prev, fields: editedFields }));
      setIsEditingFields(false);
      toast.success("Fields saved successfully");
    } catch (err) {
      toast.error("Failed to save fields");
    }
  };

  // Save table from ExcelTable component
  const handleSaveTable = async (tableId, tableData) => {
    try {
      await axios.put(`${API}/extraction/${id}/tables/${tableId}`, tableData);
      setExtraction(prev => ({
        ...prev,
        tables: prev.tables.map(t => t.id === tableId ? { ...t, ...tableData } : t)
      }));
      toast.success("Table saved successfully");
    } catch (err) {
      toast.error("Failed to save table");
    }
  };

  // Export functions
  const exportJSON = () => {
    window.open(`${API}/extraction/${id}/export/json`, '_blank');
  };

  const exportText = async () => {
    try {
      const response = await axios.get(`${API}/extraction/${id}/export/text`);
      const blob = new Blob([response.data.text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to export text");
    }
  };

  const exportTableCSV = async (tableId) => {
    try {
      const response = await axios.get(`${API}/extraction/${id}/export/csv/${tableId}`);
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded!");
    } catch (err) {
      console.error("CSV export error:", err);
      if (err.response?.status === 404) {
        toast.error("Extraction not found", {
          description: "This extraction may have been deleted. Please upload the PDF again."
        });
      } else {
        toast.error("Failed to export CSV", {
          description: err.response?.data?.detail || "Please try again."
        });
      }
    }
  };

  const exportCombinedExcel = async () => {
    try {
      const response = await axios.get(`${API}/extraction/${id}/export/excel`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${extraction?.filename?.replace('.pdf', '') || 'tables'}_structural_framing_schedules.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel file downloaded!");
    } catch (err) {
      console.error("Excel export error:", err);
      if (err.response?.status === 404) {
        toast.error("Extraction not found", {
          description: "This extraction may have been deleted. Please upload the PDF again."
        });
      } else {
        toast.error("Failed to export Excel", {
          description: err.response?.data?.detail || "Please try again."
        });
      }
    }
  };

  const exportStockList = async () => {
    try {
      const response = await axios.get(`${API}/extraction/${id}/export/stock-list`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const base = extraction?.filename?.replace('.pdf', '') || 'document';
      a.download = `${base}_stock_list.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Stock list downloaded!");
    } catch (err) {
      console.error("Stock list export error:", err);
      toast.error("Failed to download stock list", {
        description: err.response?.data?.detail || "Please try again."
      });
    }
  };

  const createProjectSheet = async () => {
    try {
      setCreatingProject(true);
      const response = await axios.post(`${API}/project-sheets?extraction_id=${id}`);
      toast.success("Project sheet created!");
      navigate(`/projects/${response.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create project sheet");
    } finally {
      setCreatingProject(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-brand-600">Loading extraction results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-brand-900 mb-2">Error Loading Results</h2>
            <p className="text-brand-600 mb-4">{error}</p>
            <Button onClick={() => navigate("/")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-50">
      <Navbar />
      
      <main className="flex-1 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-brand-200 shadow-soft p-6 mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="font-heading text-2xl font-bold text-brand-900" data-testid="document-title">
                    {extraction?.filename}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <Badge variant="secondary" data-testid="page-count-badge">
                      {extraction?.page_count} pages
                    </Badge>
                    <Badge variant="secondary">
                      {(extraction?.file_size / 1024).toFixed(1)} KB
                    </Badge>
                    {extraction?.ocr_used && (
                      <Badge className="bg-yellow-100 text-yellow-800" data-testid="ocr-badge">
                        <Eye className="w-3 h-3 mr-1" />
                        OCR Used
                      </Badge>
                    )}
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Extracted
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {extraction?.tables?.length > 0 && isAdmin && (
                  <Button 
                    onClick={createProjectSheet}
                    disabled={creatingProject}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="create-batch-btn"
                  >
                    {creatingProject ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FolderPlus className="w-4 h-4 mr-2" />
                    )}
                    Create a new Batch
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/")}
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {isAdmin ? 'New Upload' : 'Back'}
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="summary" className="animate-fade-in delay-100">
            <div className="bg-white rounded-xl border border-brand-200 shadow-soft">
              <TabsList className="w-full justify-start border-b border-brand-200 rounded-none bg-transparent p-1 gap-1">
                <TabsTrigger 
                  value="summary" 
                  className="data-[state=active]:bg-brand-100 data-[state=active]:shadow-none rounded-lg"
                  data-testid="tab-summary"
                >
                  <List className="w-4 h-4 mr-2" />
                  Summary
                </TabsTrigger>
                <TabsTrigger 
                  value="text"
                  className="data-[state=active]:bg-brand-100 data-[state=active]:shadow-none rounded-lg"
                  data-testid="tab-text"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Text
                </TabsTrigger>
                <TabsTrigger 
                  value="tables"
                  className="data-[state=active]:bg-brand-100 data-[state=active]:shadow-none rounded-lg"
                  data-testid="tab-tables"
                >
                  <Table2 className="w-4 h-4 mr-2" />
                  Tables ({extraction?.tables?.length || 0})
                </TabsTrigger>
                <TabsTrigger 
                  value="fields"
                  className="data-[state=active]:bg-brand-100 data-[state=active]:shadow-none rounded-lg"
                  data-testid="tab-fields"
                >
                  <List className="w-4 h-4 mr-2" />
                  Fields ({extraction?.fields?.length || 0})
                </TabsTrigger>
                <TabsTrigger 
                  value="download"
                  className="data-[state=active]:bg-brand-100 data-[state=active]:shadow-none rounded-lg"
                  data-testid="tab-download"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </TabsTrigger>
              </TabsList>

              {/* Summary Tab */}
              <TabsContent value="summary" className="p-6 m-0">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-brand-50 rounded-lg p-4" data-testid="summary-pages">
                    <p className="text-sm text-brand-500 mb-1">Pages</p>
                    <p className="text-2xl font-bold text-brand-900">{extraction?.page_count}</p>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4" data-testid="summary-tables">
                    <p className="text-sm text-brand-500 mb-1">Tables Found</p>
                    <p className="text-2xl font-bold text-brand-900">{extraction?.tables?.length || 0}</p>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4" data-testid="summary-fields">
                    <p className="text-sm text-brand-500 mb-1">Fields Extracted</p>
                    <p className="text-2xl font-bold text-brand-900">{extraction?.fields?.length || 0}</p>
                  </div>
                  <div className="bg-brand-50 rounded-lg p-4" data-testid="summary-chars">
                    <p className="text-sm text-brand-500 mb-1">Characters</p>
                    <p className="text-2xl font-bold text-brand-900">
                      {extraction?.pages?.reduce((acc, p) => acc + (p.char_count || 0), 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-heading font-semibold text-brand-900 mb-3">Document Metadata</h3>
                    <div className="bg-brand-50 rounded-lg p-4 space-y-2">
                      {extraction?.metadata?.title && (
                        <div className="flex justify-between">
                          <span className="text-brand-500">Title</span>
                          <span className="text-brand-900 font-medium">{extraction.metadata.title}</span>
                        </div>
                      )}
                      {extraction?.metadata?.author && (
                        <div className="flex justify-between">
                          <span className="text-brand-500">Author</span>
                          <span className="text-brand-900">{extraction.metadata.author}</span>
                        </div>
                      )}
                      {extraction?.metadata?.creator && (
                        <div className="flex justify-between">
                          <span className="text-brand-500">Creator</span>
                          <span className="text-brand-900">{extraction.metadata.creator}</span>
                        </div>
                      )}
                      {extraction?.metadata?.producer && (
                        <div className="flex justify-between">
                          <span className="text-brand-500">Producer</span>
                          <span className="text-brand-900">{extraction.metadata.producer}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Text Tab */}
              <TabsContent value="text" className="p-6 m-0">
                <div className="space-y-4">
                  {/* Search */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400" />
                      <Input
                        data-testid="text-search-input"
                        placeholder="Search in extracted text..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    {searchQuery && (
                      <span className="text-sm text-brand-500">
                        {filteredPages.length} of {extraction?.pages?.length} pages match
                      </span>
                    )}
                  </div>

                  {/* Page Navigation */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-brand-600">
                      Page {currentPage} of {extraction?.page_count}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(extraction?.page_count || 1, p + 1))}
                        disabled={currentPage === extraction?.page_count}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Text Content */}
                  <ScrollArea className="h-[500px]">
                    <div className="text-content" data-testid="text-content">
                      {extraction?.pages?.[currentPage - 1]?.text ? (
                        searchQuery 
                          ? highlightText(extraction.pages[currentPage - 1].text, searchQuery)
                          : extraction.pages[currentPage - 1].text
                      ) : (
                        <p className="text-brand-400 italic">No text content on this page</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </TabsContent>

              {/* Tables Tab */}
              <TabsContent value="tables" className="p-6 m-0">
                {extraction?.tables?.length > 0 ? (
                  <div className="space-y-6">
                    {/* Tables Summary and Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-brand-50 rounded-xl border border-brand-200">
                      <div>
                        <h3 className="font-heading font-semibold text-brand-900">
                          Structural Framing Schedules
                        </h3>
                        <p className="text-sm text-brand-600 mt-1">
                          {extraction.tables.length} table{extraction.tables.length !== 1 ? 's' : ''} extracted from this document
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={exportStockList}
                          className="border-brand-300 text-brand-700 hover:bg-brand-100"
                          data-testid="download-stock-list-btn"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download Stock List
                        </Button>
                        <Button 
                          onClick={exportCombinedExcel}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="export-combined-excel-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Combined Excel
                        </Button>
                      </div>
                    </div>

                    {/* Individual Tables */}
                    <div className="space-y-8">
                      {extraction.tables.map((table, idx) => (
                        <ExcelTable
                          key={table.id}
                          table={table}
                          tableIndex={idx}
                          onExportCSV={exportTableCSV}
                          onSaveTable={handleSaveTable}
                          isEditable={true}
                          canRename={isAdmin}
                          onRenameTable={async (tableId, newName) => {
                            try {
                              await axios.patch(`${API}/extraction/${id}/tables/${tableId}/name`, { name: newName });
                              setExtraction(prev => prev ? {
                                ...prev,
                                tables: prev.tables.map(t => t.id === tableId ? { ...t, table_name: newName, name_overridden: true } : t),
                              } : prev);
                              toast.success("Table renamed");
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to rename table");
                              throw err;
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Table2 className="w-12 h-12 text-brand-300 mx-auto mb-4" />
                    <p className="text-brand-500">No Structural Framing Schedule tables detected in this document</p>
                    <p className="text-sm text-brand-400 mt-2">
                      This extractor specifically looks for tables titled "Structural Framing Schedule"
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* Fields Tab */}
              <TabsContent value="fields" className="p-6 m-0">
                {extraction?.fields?.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-brand-600">
                        {extraction.fields.length} fields extracted
                      </span>
                      <div className="flex items-center gap-2">
                        {isEditingFields ? (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                setEditedFields(extraction.fields);
                                setIsEditingFields(false);
                              }}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                            <Button size="sm" onClick={saveFields} data-testid="save-fields-btn">
                              <Save className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                          </>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => setIsEditingFields(true)}
                            data-testid="edit-fields-btn"
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit Fields
                          </Button>
                        )}
                      </div>
                    </div>

                    <ScrollArea className="h-[400px]">
                      <div className="space-y-1">
                        {(isEditingFields ? editedFields : extraction.fields).map((field, idx) => (
                          <div key={idx} className="field-item">
                            {isEditingFields ? (
                              <>
                                <Input
                                  value={field.key}
                                  onChange={(e) => handleFieldChange(idx, 'key', e.target.value)}
                                  className="w-1/3 h-8 text-sm"
                                  placeholder="Key"
                                />
                                <Input
                                  value={field.value}
                                  onChange={(e) => handleFieldChange(idx, 'value', e.target.value)}
                                  className="w-1/2 h-8 text-sm"
                                  placeholder="Value"
                                />
                              </>
                            ) : (
                              <>
                                <span className="field-key">{field.key}</span>
                                <span className="field-value">{field.value}</span>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <List className="w-12 h-12 text-brand-300 mx-auto mb-4" />
                    <p className="text-brand-500">No key-value fields detected in this document</p>
                  </div>
                )}
              </TabsContent>

              {/* Download Tab */}
              <TabsContent value="download" className="p-6 m-0">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <button
                    onClick={exportJSON}
                    className="p-6 border border-brand-200 rounded-xl hover:bg-brand-50 hover:border-brand-300 transition-all text-left"
                    data-testid="download-json-btn"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Download className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-brand-900 mb-1">
                      Export as JSON
                    </h3>
                    <p className="text-sm text-brand-500">
                      Complete extraction data including metadata, text, tables, and fields
                    </p>
                  </button>

                  <button
                    onClick={exportText}
                    className="p-6 border border-brand-200 rounded-xl hover:bg-brand-50 hover:border-brand-300 transition-all text-left"
                    data-testid="download-text-btn"
                  >
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-brand-900 mb-1">
                      Export as TXT
                    </h3>
                    <p className="text-sm text-brand-500">
                      Plain text content from all pages
                    </p>
                  </button>

                  {extraction?.tables?.length > 0 && (
                    <button
                      onClick={() => exportTableCSV(extraction.tables[0].id)}
                      className="p-6 border border-brand-200 rounded-xl hover:bg-brand-50 hover:border-brand-300 transition-all text-left"
                      data-testid="download-csv-btn"
                    >
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                        <Table2 className="w-6 h-6 text-orange-600" />
                      </div>
                      <h3 className="font-heading font-semibold text-brand-900 mb-1">
                        Export Tables as CSV
                      </h3>
                      <p className="text-sm text-brand-500">
                        Download individual tables as CSV files
                      </p>
                    </button>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ResultsPage;
